// Admin-Panel › Audit-Log: Filter (Aktion, Akteur, Server/plattformweit), neueste oben, „Ältere laden" (before).
import { AUDIT_ACTIONS, compareSnowflakes, type AdminUserDto, type AuditLogEntryDto } from '@lagerfeuer/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { adminApi } from '../api';
import { displayName, formatDateTime } from '../format';
import { Button, Empty, Field, Loading } from '../ui';
import { useDebounced } from './hooks';
import { AdminError, AdminSection, UserPicker } from './parts';
import { ADMIN_AUDIT_KEY } from './realtime';

const PAGE_SIZE = 50;

function AuditRow(props: { entry: AuditLogEntryDto }) {
  const e = props.entry;
  const [open, setOpen] = useState(false);
  const hasData = e.data && Object.keys(e.data).length > 0;
  return (
    <>
      <tr data-testid={`admin-audit-row-${e.id}`}>
        <td>{formatDateTime(e.createdAt)}</td>
        <td>{e.actor ? `${displayName(e.actor)} (@${e.actor.username})` : 'System'}</td>
        <td className="lfx-mono" data-testid={`admin-audit-action-${e.id}`}>
          {e.action}
        </td>
        <td>
          {e.targetType ?? '–'}
          {e.targetId && <div className="lfx-muted lfx-mono">{e.targetId}</div>}
          {e.serverId && <div className="lfx-muted">Server <span className="lfx-mono">{e.serverId}</span></div>}
        </td>
        <td>{e.reason ?? '–'}</td>
        <td>
          {hasData ? (
            <button
              type="button"
              className="lfx-admin-textbtn"
              aria-expanded={open}
              data-testid={`admin-audit-details-toggle-${e.id}`}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? 'Zuklappen' : 'Details'}
            </button>
          ) : (
            '–'
          )}
        </td>
      </tr>
      {open && hasData && (
        <tr>
          <td colSpan={6}>
            {/* Details nur als Text – nie als HTML interpretieren */}
            <pre className="lfx-admin-pre" data-testid={`admin-audit-details-${e.id}`}>
              {JSON.stringify(e.data, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

export function AuditPage() {
  const [action, setAction] = useState('');
  const [actor, setActor] = useState<{ id: string; label: string } | null>(null);
  const [actorIdInput, setActorIdInput] = useState('');
  const [serverId, setServerId] = useState('');
  const [platformOnly, setPlatformOnly] = useState(false);
  const typedActorId = useDebounced(actorIdInput.trim(), 300);
  const actorId = actor?.id ?? (/^\d+$/.test(typedActorId) ? typedActorId : '');
  const server = useDebounced(platformOnly ? 'platform' : serverId.trim(), 300);
  const filters = { action: action || undefined, actorId: actorId || undefined, serverId: server || undefined };
  const list = useInfiniteQuery({
    queryKey: [...ADMIN_AUDIT_KEY, filters],
    queryFn: ({ pageParam }) => adminApi.auditLogs({ ...filters, before: pageParam, limit: PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    // Antwort aufsteigend sortiert: ältester Eintrag zuerst → dessen ID ist der nächste before-Cursor.
    getNextPageParam: (last) => (last.length < PAGE_SIZE ? undefined : last[0]?.id),
  });
  const entries = (list.data?.pages.flat() ?? []).slice().sort((a, b) => compareSnowflakes(b.id, a.id));
  return (
    <AdminSection title="Audit-Log" testId="admin-audit">
      <div className="lfx-stack">
        <div className="lfx-admin-filters">
          <Field label="Aktion">
            {(id) => (
              <select id={id} className="lfx-select" value={action} data-testid="admin-audit-action" onChange={(e) => setAction(e.target.value)}>
                <option value="">Alle Aktionen</option>
                {AUDIT_ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <div className="lfx-stack">
            {actor ? (
              <div className="lfx-field">
                <span className="lfx-field__label">Akteur</span>
                <div className="lfx-row">
                  <span data-testid="admin-audit-actor-selected">{actor.label}</span>
                  <Button testId="admin-audit-actor-clear" onClick={() => setActor(null)}>
                    Entfernen
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <UserPicker
                  testId="admin-audit-actor-picker"
                  label="Akteur suchen"
                  onSelect={(u: AdminUserDto) => setActor({ id: u.id, label: `${displayName(u)} (@${u.username})` })}
                />
                <Field label="… oder Akteur-ID">
                  {(id) => (
                    <input
                      id={id}
                      className="lfx-input lfx-mono"
                      inputMode="numeric"
                      value={actorIdInput}
                      data-testid="admin-audit-actor-id"
                      onChange={(e) => setActorIdInput(e.target.value)}
                    />
                  )}
                </Field>
              </>
            )}
          </div>
          <div className="lfx-stack">
            <Field label="Server-ID">
              {(id) => (
                <input
                  id={id}
                  className="lfx-input lfx-mono"
                  inputMode="numeric"
                  value={serverId}
                  disabled={platformOnly}
                  data-testid="admin-audit-server"
                  onChange={(e) => setServerId(e.target.value)}
                />
              )}
            </Field>
            <label className="lfx-check">
              <input type="checkbox" checked={platformOnly} data-testid="admin-audit-platform" onChange={(e) => setPlatformOnly(e.target.checked)} />
              Nur plattformweite Einträge
            </label>
          </div>
        </div>
        {list.isLoading && <Loading testId="admin-audit-loading" />}
        <AdminError error={list.error} testId="admin-audit-error" />
        {list.isSuccess && entries.length === 0 && <Empty testId="admin-audit-empty">Keine Einträge.</Empty>}
        {entries.length > 0 && (
          <div className="lfx-table-wrap">
            <table className="lfx-table" data-testid="admin-audit-list">
              <thead>
                <tr>
                  <th scope="col">Zeit</th>
                  <th scope="col">Akteur</th>
                  <th scope="col">Aktion</th>
                  <th scope="col">Ziel</th>
                  <th scope="col">Grund</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <AuditRow key={e.id} entry={e} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list.hasNextPage && (
          <Button testId="admin-audit-more" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {list.isFetchingNextPage ? 'Lädt …' : 'Ältere laden'}
          </Button>
        )}
      </div>
    </AdminSection>
  );
}
